/**
 * MUI Component Theme Overrides
 * Global component style customizations
 */

import { typography as typographyTokens } from '../tokens/dimensions';

export const components = (theme) => ({
  // Button overrides
  MuiButton: {
    styleOverrides: {
      root: {
        fontSize: theme.typography.fontSize.sm,
        textTransform: 'none',
        borderRadius: theme.shape.borderRadius,
      },
      sizeSmall: {
        fontSize: theme.typography.fontSize.sm,
        padding: theme.spacing(0.5, 1),
        height: theme.dimensions.button.small,
      },
      sizeMedium: {
        fontSize: theme.typography.fontSize.base,
        padding: theme.spacing(0.75, 2),
        height: theme.dimensions.button.medium,
      },
      sizeLarge: {
        fontSize: theme.typography.fontSize.md,
        padding: theme.spacing(1, 2.75),
        height: theme.dimensions.button.large,
      },
    },
  },
  
  // IconButton overrides
  MuiIconButton: {
    styleOverrides: {
      root: {
        padding: theme.spacing(0.5),
      },
      sizeSmall: {
        padding: theme.spacing(0.25),
      },
      sizeMedium: {
        padding: theme.spacing(1),
      },
    },
  },
  
  // TextField overrides
  MuiTextField: {
    defaultProps: {
      variant: 'outlined',
      fullWidth: true,
      size: 'small', // Default to small for density-aware scaling
    },
    styleOverrides: {
      root: {
        '& .MuiInputBase-root': {
          // Let InputBase handle the font size based on size prop
        },
        '& .MuiInputLabel-root': {
          // Label scales with the input size automatically
        },
      },
    },
  },
  
  // InputBase overrides - simplified for better density support
  MuiInputBase: {
    styleOverrides: {
      root: {
        // Default (medium) size uses base font
        fontSize: theme.typography.fontSize.base,
      },
      sizeSmall: {
        // Small size uses base font (not sm) for better readability
        fontSize: theme.typography.fontSize.base,
      },
      input: {
        // Default (medium) padding
        padding: theme.spacing(1.5, 1.75),
        height: 'auto',
      },
      inputSizeSmall: {
        // Small size gets slightly less padding but same font size
        padding: theme.spacing(1, 1.5),
        fontSize: theme.typography.fontSize.base,
      },
    },
  },
  
  // InputLabel overrides for consistent sizing
  MuiInputLabel: {
    styleOverrides: {
      root: {
        fontSize: theme.typography.fontSize.base,
      },
      shrink: {
        // Keep same size when shrunk
        fontSize: theme.typography.fontSize.base,
      },
    },
  },
  
  // Select overrides
  MuiSelect: {
    styleOverrides: {
      select: {
        fontSize: theme.typography.fontSize.base,
        padding: theme.spacing(1, 1.75),
        minHeight: theme.spacing(2),
        '&.MuiSelect-select.MuiInputBase-inputSizeSmall': {
          padding: theme.spacing(0.5, 1),
        },
      },
    },
  },
  
  // MenuItem overrides
  MuiMenuItem: {
    styleOverrides: {
      root: {
        fontSize: theme.typography.fontSize.base,
        padding: theme.spacing(0.75, 2),
        minHeight: 'auto',
      },
      dense: {
        fontSize: theme.typography.fontSize.sm,
        padding: theme.spacing(0.5, 1.5),
      },
    },
  },
  
  // FormControlLabel overrides
  MuiFormControlLabel: {
    styleOverrides: {
      root: {
        marginLeft: 0,
        marginRight: 0,
      },
      label: {
        fontSize: theme.typography.fontSize.base,
      },
    },
  },
  
  // Checkbox overrides
  MuiCheckbox: {
    styleOverrides: {
      root: {
        padding: theme.spacing(0.5),
      },
    },
  },
  
  // Radio overrides
  MuiRadio: {
    styleOverrides: {
      root: {
        padding: theme.spacing(0.5),
      },
    },
  },
  
  // Switch overrides
  MuiSwitch: {
  },
  
  // Chip overrides
  MuiChip: {
    styleOverrides: {
      root: {
        fontSize: theme.typography.fontSize.sm,
        height: theme.dimensions.chip.small,
      },
      sizeSmall: {
        fontSize: theme.typography.fontSize.xs,
        height: theme.dimensions.chip.small,
      },
      sizeMedium: {
        fontSize: theme.typography.fontSize.base,
        height: theme.dimensions.chip.medium,
      },
    },
  },
  
  // Typography overrides
  MuiTypography: {
    styleOverrides: {
      root: {
        // Default to our base font size
        fontSize: theme.typography.fontSize.base,
      },
    },
  },
  
  // Alert overrides
  MuiAlert: {
    styleOverrides: {
      root: {
        padding: theme.spacing(0.5, 2),
        fontSize: theme.typography.fontSize.base,
      },
      message: {
        padding: theme.spacing(0.5, 0),
      },
    },
  },
  
  // Paper overrides
  MuiPaper: {
    styleOverrides: {
      root: {
        backgroundImage: 'none', // Remove default gradient
      },
    },
  },
  
  // Card overrides
  MuiCard: {
    styleOverrides: {
      root: {
        borderRadius: theme.shape.borderRadius,
      },
    },
  },
  
  // CardContent overrides
  MuiCardContent: {
    styleOverrides: {
      root: {
        padding: theme.spacing(1.5),
        '&:last-child': {
          paddingBottom: theme.spacing(1.5),
        },
      },
    },
  },
  
  // Dialog overrides
  MuiDialog: {
    styleOverrides: {
      paper: {
        borderRadius: theme.shape.borderRadius,
      },
    },
  },
  
  // DialogTitle overrides
  MuiDialogTitle: {
    styleOverrides: {
      root: {
        fontSize: theme.typography.fontSize.md,
        fontWeight: typographyTokens.fontWeight.semibold,
        padding: theme.spacing(1.5),
      },
    },
  },
  
  // DialogContent overrides
  MuiDialogContent: {
    styleOverrides: {
      root: {
        padding: theme.spacing(2),
        fontSize: theme.typography.fontSize.base,
      },
    },
  },
  
  // DialogActions overrides
  MuiDialogActions: {
    styleOverrides: {
      root: {
        padding: theme.spacing(1.5),
      },
    },
  },
  
  // List overrides
  MuiList: {
    styleOverrides: {
      root: {
        padding: 0,
      },
    },
  },
  
  // ListItem overrides
  MuiListItem: {
    styleOverrides: {
      root: {
        paddingTop: theme.spacing(0.5),
        paddingBottom: theme.spacing(0.5),
      },
    },
  },
  
  // ListItemText overrides
  MuiListItemText: {
    styleOverrides: {
      primary: {
        fontSize: theme.typography.fontSize.base,
      },
      secondary: {
        fontSize: theme.typography.fontSize.sm,
      },
    },
  },
  
  // Tooltip overrides
  MuiTooltip: {
    styleOverrides: {
      tooltip: {
        fontSize: theme.typography.fontSize.sm,
        borderRadius: theme.shape.borderRadius,
      },
    },
  },
  
  // Tabs overrides
  MuiTabs: {
    styleOverrides: {
      root: {
        minHeight: 36,
      },
    },
  },
  
  // Tab overrides
  MuiTab: {
    styleOverrides: {
      root: {
        fontSize: theme.typography.fontSize.base,
        textTransform: 'none',
        minHeight: 36,
        padding: theme.spacing(0.75, 1.5),
      },
    },
  },
  
  // Table overrides
  MuiTableCell: {
    styleOverrides: {
      root: {
        fontSize: theme.typography.fontSize.sm,
        padding: theme.spacing(1, 2),
      },
      sizeSmall: {
        fontSize: theme.typography.fontSize.xs,
        padding: theme.spacing(0.5, 1),
      },
      head: {
        fontWeight: theme.typography.fontWeight.semibold,
      },
    },
  },
  
  // DataGrid overrides (MUI X)
  MuiDataGrid: {
    styleOverrides: {
      root: {
        fontSize: theme.typography.fontSize.base,
        '& .MuiDataGrid-cell': {
          fontSize: theme.typography.fontSize.sm,
        },
        '& .MuiDataGrid-columnHeaderTitle': {
          fontSize: theme.typography.fontSize.sm,
          fontWeight: typographyTokens.fontWeight.semibold,
        },
      },
    },
  },
  
  // Autocomplete overrides
  MuiAutocomplete: {
    styleOverrides: {
      root: {
        '& .MuiAutocomplete-input': {
          fontSize: theme.typography.fontSize.base,
        },
      },
      option: {
        fontSize: theme.typography.fontSize.base,
      },
      listbox: {
        '& .MuiAutocomplete-option': {
          fontSize: theme.typography.fontSize.base,
        },
      },
    },
  },
});